import { useState } from "react";
import pt from "prop-types";
import {
  trackEvent,
  EVENT_CATEGORY,
  EVENT_TYPE,
} from "@lululemon/mwa-analytics";
import { useToast } from "@lululemon/ui-library";
import { withErrorBoundary } from "@lululemon/mwa-common";
import { compose } from "@lululemon/mwa-utils";
import QuickViewModal from "../quick-view-modal";
import { useAddToBag, useFetchProductData } from "./adapter-hooks";
import {
  callSatelliteTracking,
  getErrorMessage,
  getProductData,
  getToast,
} from "./adapter-utils";
import { Logger } from "@lululemon/mwa-logger";

const logger = new Logger({
  owner: "outfitting",
  app: "outfitting-app",
});

const DAO_EVENTS = {
  CHANGE_GALLERY: "find-mine:quick-view:gallery:slide",
  COLOR_CHANGE: "find-mine:color-selection",
  SIZE_CHANGE: "find-mine:size-selection",
  LOAD: "find-mine:quick-view",
  VIEW_DETAILS: "find-mine:quick-view:product-details",
  ADD_TO_BAG: "find-mine:quick-view:add-to-bag",
};

/**
 * The component that contains the logic and API calls for the
 * QuickViewModal Component also handles analytics events
 * @param {*} param0
 * @returns
 */
const QuickViewModalAdapter = ({ config }) => {
  const [error, setError] = useState(false);
  const addToBag = useAddToBag();
  const { showToast } = useToast() || {};
  const {
    productId,
    productCategoryUnifiedId,
    productUnifiedId,
    productPosition,
    parentCategoryUnifiedId,
    isSeedProduct,
    // IIn case additional props need to be provided directly to the QVM.
    ...modalProps
  } = config || {};
  const [loading, { data, error: errorData }] = useFetchProductData({
    productId,
    productUnifiedId,
    productCategoryUnifiedId,
  });

  /**
   * Event triggered when the guests interacts with the carousel slides
   * @param {*} param0
   */
  const onSlideChange = ({ totalCount = 0, position = 0 }) => {
    callSatelliteTracking({
      id: DAO_EVENTS.CHANGE_GALLERY,
      eventCategory: EVENT_CATEGORY.COMPONENT_EVENT,
      eventSubType: EVENT_TYPE.INTERACTION,
      type: "CarouselOnIndexChange",
      position: position + 1,
      totalCount,
    });
  };

  /**
   * Event triggered when the guest interacts with the add to bag button.
   * @param {*} data
   * @returns
   */
  const onAddtoBag = async (data) => {
    const { size, sizes } = data;
    const errorMessage = getErrorMessage(sizes, undefined, false, !size);
    if (errorMessage) {
      setError(new Error(errorMessage));
      return false;
    }
    const { success, error } = await addToBag({
      ...data,
      productId,
    });
    if (error) {
      setError(error);
    }
    if (success) {
      const productName =
        data?.productSummary?.displayName || data?.productSummary?.title;
      callSatelliteTracking({
        id: DAO_EVENTS.ADD_TO_BAG,
        text: "add-to-bag",
        evars: isSeedProduct
          ? ["eVar42", "pdp:find-mine-seed-add-to-bag"]
          : ["eVar42", "pdp:find-mine-outfit-add-to-bag"],
        position: productPosition,
        type: "button",
        productList: getProductData({
          product: {
            name: productName,
          },
          selectedSku: data?.sku,
          selectedVariant: data?.selectedVariant,
          selectedSize: data?.size,
          selectedColor: data?.color,
          skus: data?.skus,
          productCategoryUnifiedId,
          parentCategoryUnifiedId,
          productUnifiedId,
          productId,
        }),
      });
      showToast(getToast(() => trackEvent("cdp-quickview-view-cart")));
      modalProps?.onClose?.();
    }
  };

  /**
   * Event triggered when the guest interacts with the color swatches.
   * @param {*} param0
   */
  const onColorChange = ({ product, ...eventInfo }) => {
    callSatelliteTracking({
      id: DAO_EVENTS.COLOR_CHANGE,
      text: product?.name,
      evars: [], // To make the event look exactly as the previous one
      productList: getProductData({
        ...eventInfo,
        product,
        productCategoryUnifiedId,
        parentCategoryUnifiedId,
        productUnifiedId,
        productId,
      }),
    });
    // The original modal resets the error when the color has changed.
    setError(null);
  };

  /**
   * Event triggered when the guest interacts with the size selector.
   * @param {*} param0
   */
  const onSizeChange = ({ product, ...eventInfo }) => {
    callSatelliteTracking({
      id: DAO_EVENTS.SIZE_CHANGE,
      text: product?.name,
      evars: [], // To make the event look exactly as the previous one
      position: productPosition,
      productList: getProductData({
        ...eventInfo,
        product,
        productCategoryUnifiedId,
        parentCategoryUnifiedId,
        productUnifiedId,
        productId,
      }),
    });
    // The original modal resets the error when the size changes
    setError(null);
  };

  /**
   * Event triggered when the guest interacts with the "view product details" link
   * @param {*} param0
   */
  const onViewDetails = ({ product, ...eventInfo }) => {
    /**
     * Prevents the next pdp to be scrolled
     */
    window.scrollTo({ top: 0, behavior: "instant" });
    callSatelliteTracking({
      id: DAO_EVENTS.VIEW_DETAILS,
      text: product?.name,
      evars: [], // To make the event look exactly as the previous one
      position: productPosition,
      productList: getProductData({
        ...eventInfo,
        product,
        productCategoryUnifiedId,
        parentCategoryUnifiedId,
        productUnifiedId,
        productId,
      }),
    });
    modalProps?.onClose?.();
  };

  /**
   * Event triggered when the modal just loaded.
   * @param {*} eventInfo
   */
  const onLoadModal = (eventInfo) => {
    callSatelliteTracking({
      id: DAO_EVENTS.LOAD,
      evars: [], // To make the event look exactly as the previous one
      position: productPosition,
      type: "link",
      productList: getProductData({
        ...eventInfo,
        productCategoryUnifiedId,
        parentCategoryUnifiedId,
        productUnifiedId,
        productId,
      }),
    });
  };

  return (
    <QuickViewModal
      {...modalProps}
      onClose={() => {
        setError(null);
        modalProps?.onClose?.();
      }}
      onSlideChange={onSlideChange}
      onAddToBag={onAddtoBag}
      onColorChange={onColorChange}
      onSizeChange={onSizeChange}
      onViewDetails={onViewDetails}
      data={data?.product?.productDetailPage}
      inventoryData={data?.inventory}
      isErrorVisible={!!error}
      error={error}
      isLoading={loading || Boolean(errorData)}
      onError={(err) => {
        setError(null);
        modalProps?.onClose?.();
        logger.error(new Error(err), {
          fingerprint: [
            "Outfitting",
            "QuickViewModal:adapter",
            "QVM-adapter",
            "Render error",
          ],
          tags: {
            componentName: "QuickViewModalAdapter",
            featureName: "Outfitting:QuickViewModal",
          },
        });
      }}
      onLoad={onLoadModal}
    />
  );
};

QuickViewModalAdapter.propTypes = {
  config: pt.shape({
    productId: pt.string.isRequired,
    productCategoryUnifiedId: pt.string.isRequired,
    productUnifiedId: pt.string,
    productPosition: pt.number,
    parentCategoryUnifiedId: pt.string,
    isSeedProduct: pt.bool,
  }),
};

export default compose(
  withErrorBoundary({ componentName: "OutfittingQuickViewModal" })
)(QuickViewModalAdapter);
